<script setup lang="ts">
import { computed, ref, useTemplateRef, onMounted, onBeforeUnmount } from 'vue';
import UiConditionalWrapper from './UiConditionalWrapper.vue';
import UiNavLink from './UiNavLink.vue';
import { isExternalHref } from '../utils/isExternalHref';

interface UiTextProps {
    as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'caption' | 'button' | 'label' | 'div';
    variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'p-small' | 'small' | 'span' | 'caption' | 'button' | 'label' | 'subtle' | 'caption-normal' | 'tooltip' | 'override';
    text?: string;
    noWrap?: boolean;
    ghost?: boolean; // The text will be invisible and aria-hidden
    truncate?: boolean | 1 | 2 | 3;
    showMoreButton?: boolean;
    class?: string | Record<string, boolean> | Array<string | Record<string, boolean>>;
    id?: string;
    href?: string;
    linkAriaLabel?: string;
}

const props = withDefaults(defineProps<UiTextProps>(), {
    as: 'p',
    text: '',
    noWrap: false,
    truncate: false,
    showMoreButton: false,
    id: '',
});

const isHeading = computed(() => props.as.startsWith('h'));
const externalHref = computed(() => isExternalHref(props.href));
const linkProps = computed(() => ({
    to: props.href && !externalHref.value ? props.href : undefined,
    href: props.href,
    external: externalHref.value || undefined,
    'aria-label': props.linkAriaLabel || undefined,
    class: 'rounded-sm hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-se-dark',
}));

const textTokens = computed((): Array<{ type: 'normal' | 'highlighted' | 'bold'; text: string }> => {
    if (!props.text) return [{ type: 'normal', text: '' }];

    // Check if text has any markdown formatting
    const hasMarkdown = props.text.includes('*');
    if (!hasMarkdown) {
        return [{ type: 'normal', text: props.text }];
    }

    // TODO (Khaled): Refactor this to use a more efficient approach. Not that this one is not but I didn't spend much time thinking about it and implemented the first thing that came to mind.
    const result: Array<{ type: 'normal' | 'highlighted' | 'bold'; text: string }> = [];

    // For headings: match both ** (emphasis) and * (bold)
    // For other text: match only * (bold)
    const regex = isHeading.value
        ? /\*\*(.*?)\*\*|\*(.*?)\*/g  // Match ** first, then *
        : /\*(.*?)\*/g;                // Match only *

    let lastIndex = 0;
    let match;

    while ((match = regex.exec(props.text)) !== null) {
        if (match.index > lastIndex) {
            const normalText = props.text.slice(lastIndex, match.index);
            if (normalText) {
                result.push({ type: 'normal' as const, text: normalText });
            }
        }

        if (isHeading.value) {
            // For headings: match[1] is ** pattern (emphasis), match[2] is * pattern (bold)
            if (match[1] !== undefined) {
                result.push({ type: 'highlighted' as const, text: match[1] });
            } else if (match[2] !== undefined) {
                result.push({ type: 'bold' as const, text: match[2] });
            }
        } else {
            // For other text: match[1] is * pattern (bold only)
            if (match[1] !== undefined) {
                result.push({ type: 'bold' as const, text: match[1] });
            }
        }

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < props.text.length) {
        const remainingText = props.text.slice(lastIndex);
        if (remainingText) {
            result.push({ type: 'normal' as const, text: remainingText });
        }
    }


    return result;
});

const dynamicClasses = computed(() => {
    let variantStyle = '';
    switch (props.variant || props.as) {
        case 'h1':
            variantStyle = 'se-font-h1 font-semibold';
            break;
        case 'h2':
            variantStyle = 'se-font-h2';
            break;
        case 'h3':
            variantStyle = 'se-font-h3';
            break;
        case 'h4':
            variantStyle = 'se-font-h4';
            break;
        case 'h5':
            variantStyle = 'se-font-h5';
            break;
        case 'h6':
            variantStyle = 'se-font-h6';
            break;
        case 'p':
        case 'span':
        case 'div':
            variantStyle = 'se-font-p';
            break;
        case 'p-small':
            variantStyle = 'se-font-p-small';
            break;
        case 'small':
            variantStyle = 'se-font-small';
            break;
        case 'caption':
            variantStyle = 'se-font-caption uppercase';
            break;
        case 'caption-normal':
            variantStyle = 'se-font-caption';
            break;
        case 'button':
            variantStyle = 'se-font-button';
            break;
        case 'label':
            variantStyle = 'se-font-p';
            break;
        case 'subtle':
            variantStyle = 'se-font-p text-sm text-gray-500';
            break;
        case 'tooltip':
            variantStyle = 'se-font-tooltip';
            break;
        case 'override':
            variantStyle = '';
            break;
        default:
            variantStyle = 'se-font-p';
            break;
    }
    return `${variantStyle} ${props.noWrap ? 'whitespace-nowrap max-w-full' : 'whitespace-pre-wrap'} ${props.ghost ? 'invisible pointer-events-none select-none' : ''}`;
});

const expanded = ref(false);

const isTruncated = computed(() => {
    return !props.showMoreButton || !expanded.value;
});

const textRef = useTemplateRef<HTMLDivElement | null>('textRef');
const isClamped = ref(false);
const resizeObserver = ref<ResizeObserver | null>(null);

onMounted(async () => {
    resizeObserver.value = new ResizeObserver(() => {
        if (textRef.value?.clientHeight && textRef.value?.scrollHeight) {
            isClamped.value = textRef.value.clientHeight < textRef.value.scrollHeight;
            if (isClamped.value) {
                resizeObserver.value?.disconnect();
                resizeObserver.value = null;
            }
        }
    });

    if (textRef.value) {
        resizeObserver.value?.observe(textRef.value);
    }
});

onBeforeUnmount(() => {
    if (resizeObserver.value) {
        resizeObserver.value.disconnect();
        resizeObserver.value = null;
    }
});

const truncateClasses = computed(() => {
    if (props.truncate && typeof props.truncate === 'number') {
        let truncateClass = '';
        switch (props.truncate) {
            case 1:
                truncateClass = 'line-clamp-1';
                break;
            case 2:
                truncateClass = 'line-clamp-2';
                break;
            case 3:
                truncateClass = 'line-clamp-3';
                break;
        }
        return isTruncated.value ? truncateClass : `actually-${truncateClass}`;
    }
});
</script>

<template>
    <UiConditionalWrapper :condition="!!truncate" :wrapper="'div'" :wrapper-props="{ class: props.class }">
        <UiConditionalWrapper :condition="!!props.href" :wrapper="UiNavLink" :wrapper-props="linkProps">
            <component :is="as === 'caption' || as === 'button' ? 'span' : as" class="font-archivo"
                :class="[dynamicClasses, truncateClasses, truncate ? '' : props.class]" ref="textRef"
                :aria-hidden="props.ghost" :role="props.ghost ? 'presentation' : undefined" :id="props.id">
                <template v-if="textTokens && textTokens.length === 1 && textTokens[0]?.type === 'normal'">
                    {{ textTokens[0].text }}
                </template>
                <template v-else-if="textTokens">
                    <template v-for="(token, index) in textTokens" :key="index">
                        <span v-if="token.type === 'highlighted'" class="font-besley italic">{{ token.text }}</span>
                        <strong v-else-if="token.type === 'bold'">{{ token.text }}</strong>
                        <template v-else>{{ token.text }}</template>
                    </template>
                </template>
                <slot />
            </component>
        </UiConditionalWrapper>
        <span v-if="showMoreButton && isClamped" @click="expanded = !expanded"
            class="font-semibold cursor-pointer font-archivo" :class="[dynamicClasses, props.class]">{{ expanded ? 'Show less' : 'Show more' }}</span>
    </UiConditionalWrapper>
</template>
